import React, { useEffect, useMemo } from 'react';
import { Text, TouchableOpacity, View, StyleSheet, Animated, Linking, ScrollView } from 'react-native';
import type { ImageStyle } from 'react-native';
import useKeyboardFormAnimation from 'src/presentation/hooks/useKeyboardFormAnimation';
import { useContentWidth } from 'src/presentation/hooks/useContentWidth';
import type { RouteProp } from '@react-navigation/native';
import { useNavigation, useRoute } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import NavBar from 'src/presentation/components/organisms/NavBar';
import Heading from 'src/presentation/components/atoms/Heading';
import LoginForm from 'src/presentation/components/organisms/forms/LoginForm';
import { useNavigationStore } from 'src/domain/stores/useNavigationStore';
import { useAuthenticationStore } from 'src/domain/stores/useAuthenticationStore';
import { useFormStore } from 'src/domain/stores/useFormStore';
import { useEventStore } from 'src/domain/stores/useEventStore';
import Images from 'src/core/utils/Images';
import Colors from 'src/core/utils/Colors';
import Dimens from 'src/core/utils/Dimens';
import Fonts from 'src/core/utils/Fonts';
import Translations from 'src/core/utils/Translations';
import type { RootStackParamList } from 'src/presentation/navigation/types/NavigationTypes';
import Constants from 'src/core/utils/Constants';

function LoginFormScreen() {
	const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
	const route = useRoute<RouteProp<RootStackParamList, typeof Constants.screenLoginForm>>();
	const navigationStore = useNavigationStore();
	const authStore = useAuthenticationStore();
	const formStore = useFormStore();
	const eventStore = useEventStore();
	const insets = useSafeAreaInsets();

	const { keyboardHeight, imageHeight } = useKeyboardFormAnimation();
	const contentWidth = useContentWidth();

	const sizedStyles = useMemo((): { img: ImageStyle } => ({ img: { width: contentWidth } }), [contentWidth]);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect(() => {
		const subscription = Linking.addEventListener('url', handleOpenURL);
		return () => subscription.remove();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const onLogin = (routeName: string) => {
		if (routeName === Constants.screenCustom) {
			navigation.goBack();
		} else if (useAuthenticationStore.getState().isLoggedin) {
			// Read the current login status from the store, not from the render closure: onLogin draait synchroon ná
			// login() maar vóór de re-render, dus `authStore.isLoggedin` would still have the old (stale) value here
			// -> navigation only on the second tap.
			setTimeout(() => {
				navigation.reset({
					index: 0,
					routes: [{ name: Constants.screenIndex }],
				});
			}, 0);
		}
	};

	const onResetPassword = (routeName: string) => {
		(navigation.navigate as (name: string) => void)(routeName);
	};

	const handleOpenURL = (event: { url: string }) => {
		if (event.url) {
			const token = getParamValueFromURL('token', event.url);
			navigation.navigate(Constants.screenChangePassword, {
				token,
			});
		} else if (!navigationStore.fromNotification) {
			setTimeout(() => {
				navigationStore.reset(authStore.initialRoute);
			}, 1000);
		}
	};

	const getParamValueFromURL = (name: string, url: string) => {
		name = name.replace(/\[/, '\\[').replace(/\]/, '\\]');
		const regexS = '[\\?&]' + name + '=([^&#]*)';
		const regex = new RegExp(regexS);
		const results = regex.exec(url);
		return results === null ? null : results[1];
	};

	return (
		<View style={styles.root}>
			{formStore.loginFromForm ? (
				<NavBar
					backBtn={true}
					bgImage={eventStore.activeTheme.header_bg_image}
					bgcolor={eventStore.activeTheme.header_bg_color}
					color={eventStore.activeTheme.header_text_color}
					editBtn={false}
					statusBarStyle={eventStore.activeTheme.statusbar_color}
					title={Translations.buttonLogin}
				/>
			) : (
				<NavBar
					backBtn={true}
					bgcolor={Colors.primary}
					centerTitle={true}
					color={Colors.black}
					editBtn={false}
					title={Translations.buttonLogin}
				/>
			)}
			<ScrollView
				contentContainerStyle={{ paddingBottom: insets.bottom + Dimens.padding16 }}
				keyboardShouldPersistTaps="always"
				style={styles.scroll}
			>
				<Animated.View style={{ paddingBottom: keyboardHeight }}>
					{(route?.name as string) === Constants.screenProfileLoginPage ||
					(route?.name as string) === Constants.screenLostPasswordPage ? null : (
						<Animated.Image
							resizeMode="cover"
							source={Images.loginHeader}
							style={[sizedStyles.img, { height: imageHeight }]}
						/>
					)}
					<View style={styles.contentContainer}>
						<Heading text={Translations.buttonLogin} />
					</View>
					{(route?.name as string) === Constants.screenProfileLoginPage ||
					(route?.name as string) === Constants.screenLostPasswordPage ? (
						<LoginForm
							profile={true}
							onLogin={(routeName: string) => onLogin(routeName)}
							onResetPassword={(routeName: string) => onResetPassword(routeName)}
						/>
					) : (
						<LoginForm
							onLogin={(routeName: string) => onLogin(routeName)}
							onResetPassword={(routeName: string) => onResetPassword(routeName)}
						/>
					)}
					{(route?.name as string) === Constants.screenProfileLoginPage ||
					(route?.name as string) === Constants.screenLostPasswordPage ? null : (
						<View style={styles.registerContainer}>
							<Text style={styles.textLinkHelper}>{Translations.textNoAccount}</Text>
							<TouchableOpacity
								onPress={() => {
									navigation.goBack();
									navigation.navigate(Constants.screenRegistrationForm);
								}}
							>
								<Text style={styles.textLink}>{Translations.textRegisterLink}</Text>
							</TouchableOpacity>
						</View>
					)}
				</Animated.View>
			</ScrollView>
		</View>
	);
}

export default LoginFormScreen;

const styles = StyleSheet.create({
	root: {
		flex: 1,
		backgroundColor: Colors.white,
	},
	scroll: {
		backgroundColor: Colors.white,
	},
	textLinkHelper: {
		color: Colors.lightGrey,
	},
	textLink: {
		color: Colors.black,
		fontFamily: Fonts.montserratBold,
	},
	contentContainer: {
		paddingLeft: Dimens.padding20,
		paddingRight: Dimens.padding20,
		paddingTop: Dimens.padding20,
	},
	registerContainer: {
		flexDirection: 'row',
		justifyContent: 'center',
		marginBottom: Dimens.margin20,
	},
});
